import React from 'react';
import styles from './CadIncidentInformation.module.scss';
import {
  CadAddressValueType,
  CadIncidentValueType,
  CadOffenseValueType,
  CadPersonValueType,
  CadPremiseValueType
} from '../../../../../Models/CadReportValue';
import {
  FieldItemList,
  FieldItemListType
} from '../../CommonBody/FieldItemList/FieldItemList';
import { FieldValueType } from '../../../../../Models';
import {
  getCADAddressFull,
  getCADOffenseLabel,
  getCADPersonnelFullName,
  getCADPremiseName
} from '../../CommonBody/Helpers';
import get from 'lodash.get';
import { formatDate } from '../../../../../Helpers';
import { Customer, CustomerContext } from 'ui-smartforce-settings';

const getCADNarrativeLabel = (
  narrative: { date_time: FieldValueType; badge_id: string },
  personnel: CadPersonValueType[],
  timezone: string
): string => {
  const dateTime = formatDate(
    get(narrative, 'date_time', '') as string,
    'L - HH:mm',
    timezone
  );
  const personnelName = getCADPersonnelFullName(narrative.badge_id, personnel);
  return `${dateTime} - ${personnelName}`;
};

const getIncidentFields = (
  incident: CadIncidentValueType,
  addresses?: CadAddressValueType[],
  premises?: CadPremiseValueType[],
  offenses?: CadOffenseValueType[],
  personnel?: CadPersonValueType[],
  timezone?: string
): FieldItemListType[] => {
  let incidentFields: FieldItemListType[] = [];

  incidentFields = [
    {
      label: 'Agency',
      value: incident.agency,
      type: 'string'
    },
    {
      label: 'Date and Time - Earliest Occurrence',
      value: incident.date_time_occurred_earliest,
      type: 'datetime'
    },
    {
      label: 'Date and Time - Latest Occurrence',
      value: incident.date_time_occurred_latest,
      type: 'datetime'
    },
    {
      label: 'Date and Time - Disposition',
      value: incident.date_time_disposition,
      type: 'datetime'
    }
  ];

  if (incident.premise_id) {
    incidentFields.push({
      label: 'Premise',
      value: getCADPremiseName(incident.premise_id, premises || []),
      type: 'string'
    });
  }

  if (incident.address_id) {
    incidentFields.push({
      label: 'Address',
      value: getCADAddressFull(incident.address_id, addresses || []),
      type: 'string'
    });
  }

  if (
    incident.offenses_ids &&
    incident.offenses_ids.length > 0 &&
    offenses &&
    offenses.length > 0
  ) {
    incidentFields.push({
      label: 'Offenses',
      value: incident.offenses_ids.map((id) => {
        return {
          label: '',
          value: getCADOffenseLabel(id, offenses),
          type: 'string'
        };
      }),
      type: 'subList'
    });
  }

  incidentFields = [
    ...incidentFields,
    {
      label: 'MO', // Modus Operandi
      value: incident.mo,
      type: 'string'
    }
  ];

  if (
    incident.officers_badge_ids &&
    incident.officers_badge_ids.length > 0 &&
    personnel
  ) {
    incidentFields.push({
      label: 'Officers Involved',
      value: incident.officers_badge_ids.map((badgeId) => {
        return {
          label: '',
          value: getCADPersonnelFullName(badgeId, personnel || []),
          type: 'string'
        };
      }),
      type: 'subList'
    });
  }

  if (incident.narratives && incident.narratives.length > 0 && personnel) {
    incidentFields.push({
      label: 'Narratives',
      value: incident.narratives.map((narrative) => {
        return {
          label: getCADNarrativeLabel(
            narrative,
            personnel || [],
            timezone || ''
          ),
          value: narrative.narrative,
          type: 'string'
        };
      }),
      type: 'subList'
    });
  }

  return [
    ...incidentFields,
    {
      label: 'Disposition',
      value: incident.disposition,
      type: 'string'
    },
    {
      label: 'Status',
      value: incident.status,
      type: 'string'
    }
  ];
};

export interface CadIncidentInformationProps {
  incidents: CadIncidentValueType[];
  addresses?: CadAddressValueType[];
  premises?: CadPremiseValueType[];
  offenses?: CadOffenseValueType[];
  personnel?: CadPersonValueType[];
}

export const CadIncidentInformation = ({
  incidents,
  addresses,
  premises,
  offenses,
  personnel
}: CadIncidentInformationProps): React.ReactElement<CadIncidentInformationProps> => {
  const { timezone } = React.useContext(CustomerContext).customer as Customer;

  return (
    <div className={styles.cadIncidentInformation}>
      <h3 className={styles.title}>Incident Information</h3>
      <div className={styles.items}>
        {incidents.map((incident, index) => (
          <FieldItemList
            key={incident.id}
            title={`${index === 0 ? 'Nº ' + incident.case_number : ''}`}
            fields={getIncidentFields(
              incident,
              addresses,
              premises,
              offenses,
              personnel,
              timezone
            )}
            hasDivider={index < incidents.length - 1}
          />
        ))}
      </div>
    </div>
  );
};
